import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import CopyVariable from '../../../../common/CopyVariable';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import ArrowValueIcon from './ArrowValue.icon';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioSide from './scenario-side';
import { TextInputWithHint } from './TextInputWithHint';

interface IFormData extends INoteForm {
  name: string;
  variableName: string;
  value: string;
}

export const MacBotCreateVariableForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const [isDelete, setIsDelete] = useState(true);
  const [rows, setRows] = useState(1);

  const userId = useAppSelector(selectUseId);

  const schema = Yup.object({
    name: Yup.string().max(30, t('Wizard/validationLabels/key16')).required(t('MacBotCreateVariableForm/scenario_variable_name_required')),
    variableName: Yup.string().required(t('MacBotCreateVariableForm/scenario_variable_required')),
    value: Yup.string().required(t('MacBotCreateVariableForm/scenario_value_required')),
    ...getNoteSchema(t),
  });
  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    control,
    getValues,
    watch,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: '',
      variableName: '',
      value: '',
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });

  const { fields, append } = useFieldArray({
    control,
    name: 'notes',
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    let value;
    try {
      value = JSON.parse(data.value);
    } catch (ex) {
      value = data.value;
    }
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        type: BlockType.CreateVariable,
        parent: itemData.parent,
        ...data,
        title: data.name,
        isPrivate: false,
        value,
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.CreateVariable,
        parent: parentId,
        ...data,
        title: data.name,
        isPrivate: false,
        value,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        name: itemData?.name ?? '',
        variableName: itemData?.variableName ?? '',
        value: itemData?.value ?? '',
        notes: itemData?.notes,
      });
      const lineCount = String(itemData?.value)?.split('\n').length;
      setRows(lineCount);
    }
  }, [itemData]);

  const value = watch('value');
  useEffect(() => {
    if (watch('value') !== undefined) {
      const lineCount = String(value).split('\n').length;
      setRows(lineCount > 0 ? lineCount : 1);
    }
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(onSubmit)();
    } else if (e.key === 'Enter' && e.shiftKey) {
      // Shift + Enter → add a new line and increase rows
      setRows(prev => prev + 1);
    }
  };

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(onSubmit)} size="md">
        <ScenarioHeader
          type={MacBotActionKind.ShowCreateVariableForm}
          onClose={() => {
            reset();
          }}
        />
        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <Col xs={12}>
              <TextInputWithHint
                label={t('MacBotCreateVariableForm/key2')}
                name="name"
                register={register(`name`, {
                  onChange: e => {
                    setValue(`variableName`, `${e.target.value.trim().toLowerCase().replaceAll(' ', '_')}`);
                  },
                })}
                error={errors.name}
                max={31}
                maxLength={31}
                HintMessage={[t('MacBotMediaForm/maxSizeLenght', { size: 30 })]}
              />
            </Col>
          </Row>

          <Row className="tw-mt-2 tw-items-center g-md-3 md:tw-gap-y-0 tw-gap-y-3">
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotCreateVariableForm/key4')}
                </Form.Label>
                <Controller name="variableName" control={control} render={({ field: { value } }) => <CopyVariable text={value} noTitle />} />
              </Form.Group>
            </Col>
            <Col xs={12} className="md:tw-pt-8 md:tw-pb-0 tw-pt-4 tw-pb-4 tw-text-center">
              <ArrowValueIcon />
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('MacBotCreateVariableForm/key7')}
                </Form.Label>
                <Form.Control
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300  tw-resize-none"
                  isInvalid={!!errors.value}
                  placeholder={t('MacBotCreateVariableForm/key8').toString()}
                  {...register(`value`, {})}
                  as="textarea"
                  rows={rows}
                  onKeyDown={handleKeyDown}
                />

                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors.value?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-submit-create-variable" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>
        <NoteForm noteFormMethods={{ register, watch, errors, fields, setValue }} />
      </ScenarioSide>
    </React.Fragment>
  );
};
